import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function NewsSection() {
  const { t } = useTranslation('common');
  const [selectedNews, setSelectedNews] = useState<number | null>(null);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const featuredNews = {
    id: 1,
    title: t('news.news1Title'),
    excerpt: t('news.news1Desc'),
    category: t('hero.europeanStandards'),
    date: '15 Ocak 2025',
    image: 'https://public.readdy.ai/ai/img_res/e114ead4795a17758a3faecd03876307.jpg',
    fullContent: `
      <h2 class="text-3xl font-bold mb-6">Sürdürülebilir Mimarlıkta Yeni Trendler</h2>
      
      <p class="mb-4">Günümüzde mimarlık dünyası, çevre dostu ve sürdürülebilir tasarım anlayışıyla hızla dönüşüyor. İklim değişikliği ve çevre sorunlarının artmasıyla birlikte, mimarlar ve tasarımcılar doğaya saygılı, enerji verimli ve uzun ömürlü yapılar inşa etmeye odaklanıyor.</p>
      
      <h3 class="text-2xl font-semibold mt-8 mb-4">Doğal Malzemelerin Yükselişi</h3>
      <p class="mb-4">Ahşap, bambu, kil ve taş gibi doğal malzemeler, modern mimarlıkta yeniden popülerlik kazanıyor. Bu malzemeler sadece estetik açıdan çekici olmakla kalmıyor, aynı zamanda karbon ayak izini azaltıyor ve binalara doğal bir ısı yalıtımı sağlıyor. Özellikle çapraz lamine ahşap (CLT) teknolojisi, yüksek katlı ahşap binaların inşasını mümkün kılarak çelik ve betonun yerini alıyor.</p>
      
      <h3 class="text-2xl font-semibold mt-8 mb-4">Pasif Ev Standartları</h3>
      <p class="mb-4">Pasif ev konsepti, enerji tüketimini minimize eden yapı tasarımlarını ifade ediyor. Yüksek kaliteli yalıtım, hava geçirmez yapı kabuğu ve ısı geri kazanımlı havalandırma sistemleri sayesinde bu binalar, geleneksel yapılara göre %90'a varan enerji tasarrufu sağlıyor. Avrupa'da yaygınlaşan bu standart, Türkiye'de de giderek daha fazla proje tarafından benimseniyor.</p>
      
      <h3 class="text-2xl font-semibold mt-8 mb-4">Yeşil Çatılar ve Dikey Bahçeler</h3>
      <p class="mb-4">Kentsel alanlarda yeşil alanların azalmasıyla birlikte, yeşil çatılar ve dikey bahçeler önem kazanıyor. Bu uygulamalar, binaların ısı yalıtımını artırırken, hava kalitesini iyileştiriyor ve kentsel ısı adası etkisini azaltıyor. Ayrıca, yağmur suyu yönetiminde de önemli bir rol oynuyor.</p>
      
      <h3 class="text-2xl font-semibold mt-8 mb-4">Yenilenebilir Enerji Entegrasyonu</h3>
      <p class="mb-4">Güneş panelleri, rüzgar türbinleri ve jeotermal sistemler, modern binaların vazgeçilmez unsurları haline geliyor. Akıllı enerji yönetim sistemleri ile entegre edilen bu teknolojiler, binaların enerji ihtiyacını karşılarken, fazla enerjiyi şebekeye geri satma imkanı sunuyor.</p>
      
      <h3 class="text-2xl font-semibold mt-8 mb-4">Döngüsel Ekonomi ve Geri Dönüşüm</h3>
      <p class="mb-4">Sürdürülebilir mimarlık, yapı malzemelerinin yaşam döngüsünü de göz önünde bulunduruyor. Geri dönüştürülmüş malzemelerin kullanımı, yapı atıklarının minimize edilmesi ve binaların sökülüp yeniden kullanılabilir olması, döngüsel ekonomi anlayışının mimarlıktaki yansımaları olarak karşımıza çıkıyor.</p>
      
      <p class="mt-8 text-gray-600 italic">Sürdürülebilir mimarlık, sadece bir trend değil, geleceğimiz için bir zorunluluk. ERNADO olarak, tüm projelerimizde çevre dostu tasarım prensiplerini benimseyerek, daha yaşanabilir bir dünya için katkıda bulunuyoruz.</p>
    `
  };

  const smallNews = [
    {
      id: 2,
      title: t('news.news2Title'),
      excerpt: t('news.news2Desc'),
      category: t('furniture.title'),
      date: '12 Ocak 2025',
      image: 'https://readdy.ai/api/search-image?query=organic%20curved%20furniture%20design%20amorphous%20shapes%20modern%20interior%20minimalist%20background%20flowing%20architectural%20forms%20simple%20clean%20studio%20shot&width=800&height=600&seq=news-2&orientation=landscape',
      fullContent: `
        <h2 class="text-3xl font-bold mb-6">Biophilic Tasarım: Doğa ile İç İçe Yaşam</h2>
        
        <p class="mb-4">Biophilic tasarım, insanların doğayla olan doğuştan gelen bağını iç mekanlara taşıyan bir tasarım felsefesidir. Bu yaklaşım, modern yaşamın stresini azaltırken, sağlık ve refahı artırmayı hedefliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Doğal Işık ve Havalandırma</h3>
        <p class="mb-4">Geniş pencereler, cam tavanlı atriumlar ve ışıklıklar sayesinde iç mekanlara bol miktarda doğal ışık alınıyor. Doğal ışık, insan sağlığı üzerinde olumlu etkiler yaratırken, enerji tüketimini de azaltıyor. Çapraz havalandırma sistemleri ile doğal hava akışı sağlanarak, mekanik havalandırma ihtiyacı minimize ediliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">İç Mekan Bitkileri ve Yeşil Duvarlar</h3>
        <p class="mb-4">Ofislerde, evlerde ve kamusal alanlarda iç mekan bitkileri ve yeşil duvarlar giderek yaygınlaşıyor. Bitkiler, hava kalitesini iyileştirirken, stres seviyelerini düşürüyor ve üretkenliği artırıyor. Özellikle dikey bahçeler, sınırlı alanlarda maksimum yeşil alan yaratmanın etkili bir yolu.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Doğal Malzemeler ve Dokular</h3>
        <p class="mb-4">Ahşap, taş, bambu ve keten gibi doğal malzemeler, iç mekanlara sıcaklık ve doku katıyor. Bu malzemeler, görsel olarak çekici olmakla kalmayıp, dokunsal bir deneyim de sunuyor. Doğal renk paletleri ve organik formlar, mekanları daha huzurlu ve davetkar hale getiriyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Su Öğeleri</h3>
        <p class="mb-4">İç mekan çeşmeleri, akvaryumlar ve su duvarları, doğanın sakinleştirici etkisini iç mekanlara taşıyor. Su sesi, beyni rahatlatırken, nem dengesini de olumlu yönde etkiliyor. Özellikle ofis ve spa alanlarında su öğeleri tercih ediliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Doğa Manzaraları ve Görsel Bağlantı</h3>
        <p class="mb-4">Pencerelerden doğa manzarası, iç mekanlarda doğa temalı sanat eserleri ve fotoğraflar, insanların doğayla görsel bağ kurmasını sağlıyor. Araştırmalar, doğa manzarasına sahip ofislerde çalışanların daha mutlu ve üretken olduğunu gösteriyor.</p>
        
        <p class="mt-8 text-gray-600 italic">ERNADO olarak, biophilic tasarım prensiplerini tüm projelerimize entegre ederek, insanların doğayla bağını güçlendiren mekanlar yaratıyoruz.</p>
      `
    },
    {
      id: 3,
      title: t('news.news3Title'),
      excerpt: t('news.news3Desc'),
      category: t('exterior.prefabTitle'),
      date: '10 Ocak 2025',
      image: 'https://readdy.ai/api/search-image?query=modern%20prefabricated%20building%20architecture%20modular%20construction%20elegant%20design%20contemporary%20structure%20simple%20clean%20background%20minimalist%20exterior&width=800&height=600&seq=news-3&orientation=landscape',
      fullContent: `
        <h2 class="text-3xl font-bold mb-6">Akıllı Ev Teknolojileri 2025</h2>
        
        <p class="mb-4">Yapay zeka ve nesnelerin interneti (IoT) teknolojileri, evlerimizi daha konforlu, güvenli ve enerji verimli hale getiriyor. 2025 yılında akıllı ev teknolojileri, sadece lüks değil, standart bir ihtiyaç haline geliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Yapay Zeka Destekli Ev Asistanları</h3>
        <p class="mb-4">Yeni nesil ev asistanları, kullanıcıların alışkanlıklarını öğrenerek proaktif önerilerde bulunuyor. Sabah kahvenizi hazırlamaktan, ışıkları ayarlamaya, müzik çalmaktan, alışveriş listesi oluşturmaya kadar birçok görevi sesli komutlarla gerçekleştirebiliyorsunuz. Gelişmiş doğal dil işleme yetenekleri sayesinde, bu asistanlar artık daha doğal ve akıcı konuşmalar yapabiliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Akıllı Enerji Yönetimi</h3>
        <p class="mb-4">Akıllı termostatlar, enerji monitörleri ve otomatik perde sistemleri, evin enerji tüketimini optimize ediyor. Yapay zeka algoritmaları, hava durumu tahminlerini, elektrik tarifelerini ve kullanıcı alışkanlıklarını analiz ederek en verimli enerji kullanımını sağlıyor. Güneş panelleri ve ev bataryaları ile entegre sistemler, enerji maliyetlerini %70'e varan oranlarda düşürebiliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Gelişmiş Güvenlik Sistemleri</h3>
        <p class="mb-4">Yüz tanıma teknolojisi, akıllı kilitler ve AI destekli güvenlik kameraları, ev güvenliğini yeni bir seviyeye taşıyor. Sistemler, tanıdık yüzleri ve şüpheli davranışları ayırt edebiliyor, acil durumlarda otomatik olarak yetkilileri uyarabiliyor. Akıllı kapı zilleri, ziyaretçilerle uzaktan görüşme imkanı sunuyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Sağlık ve Wellness Teknolojileri</h3>
        <p class="mb-4">Akıllı aynalar, uyku takip sistemleri ve hava kalitesi sensörleri, ev sakinlerinin sağlığını izliyor. Akıllı duşlar, su sıcaklığını ve basıncını kişiselleştirirken, akıllı yataklar uyku kalitesini optimize ediyor. Hava kalitesi sensörleri, CO2, nem ve kirletici seviyeleri izleyerek havalandırma sistemlerini otomatik olarak ayarlıyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Akıllı Mutfak Teknolojileri</h3>
        <p class="mb-4">Akıllı buzdolapları, içindeki ürünleri takip ederek alışveriş listesi oluşturuyor ve son kullanma tarihlerini hatırlatıyor. Akıllı fırınlar, yemek tariflerini otomatik olarak uygularken, akıllı kahve makineleri sabah rutininize göre kahvenizi hazırlıyor. Sesli asistanlar, yemek tariflerini adım adım anlatarak mutfakta rehberlik ediyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Entegre Ekosistem</h3>
        <p class="mb-4">Tüm akıllı cihazlar, merkezi bir platform üzerinden yönetiliyor ve birbirleriyle iletişim kuruyor. Örneğin, alarm çaldığında perdeler açılıyor, kahve makinesi çalışmaya başlıyor ve termostat sıcaklığı artırıyor. Bu entegrasyon, kullanıcı deneyimini önemli ölçüde iyileştiriyor.</p>
        
        <p class="mt-8 text-gray-600 italic">ERNADO olarak, projelerimizde en son akıllı ev teknolojilerini entegre ederek, geleceğin evlerini bugünden inşa ediyoruz.</p>
      `
    },
    {
      id: 4,
      title: 'AR Teknolojisi Mobilya Alışverişini Değiştiriyor',
      excerpt: 'Artırılmış gerçeklik, müşterilerin satın alma öncesi ürünleri deneyimlemesini sağlıyor.',
      category: t('hero.arTechnology'),
      date: '8 Ocak 2025',
      image: 'https://readdy.ai/api/search-image?query=augmented%20reality%20technology%20in%20interior%20design%20AR%20furniture%20visualization%20modern%20digital%20interface%20simple%20clean%20background%20futuristic%20concept&width=800&height=600&seq=news-4&orientation=landscape',
      fullContent: `
        <h2 class="text-3xl font-bold mb-6">AR Teknolojisi Mobilya Alışverişini Değiştiriyor</h2>
        
        <p class="mb-4">Artırılmış gerçeklik (AR) teknolojisi, mobilya ve iç dekorasyon sektöründe devrim yaratıyor. Müşteriler, satın almadan önce ürünleri kendi mekanlarında görüntüleyerek daha bilinçli kararlar verebiliyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Sanal Deneme Deneyimi</h3>
        <p class="mb-4">AR uygulamaları sayesinde, müşteriler akıllı telefonları veya tabletleri ile mobilyaları gerçek boyutlarında ve gerçek mekanlarında görüntüleyebiliyor. Bir koltuk, masa veya lamba seçerken, ürünün odaya uyup uymadığını, renk uyumunu ve boyut oranlarını anında kontrol edebiliyorlar. Bu teknoloji, yanlış satın alma riskini önemli ölçüde azaltıyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Kişiselleştirme İmkanları</h3>
        <p class="mb-4">AR platformları, müşterilerin mobilya renklerini, dokularını ve konfigürasyonlarını değiştirerek kişiselleştirmesine olanak tanıyor. Farklı kumaş seçeneklerini, ahşap tonlarını veya metal yüzeyleri sanal ortamda deneyerek en uygun kombinasyonu bulabiliyorlar. Bu özellik, özellikle özel tasarım mobilya siparişlerinde büyük kolaylık sağlıyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Ölçüm ve Planlama Araçları</h3>
        <p class="mb-4">Gelişmiş AR uygulamaları, otomatik ölçüm araçları sunuyor. Kamera ile odayı tarayarak, duvar uzunluklarını, tavan yüksekliğini ve mevcut mobilyaların boyutlarını otomatik olarak ölçebiliyorsunuz. Bu veriler, yeni mobilyaların mekana uyumunu planlamada kritik öneme sahip.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Sosyal Paylaşım ve İşbirliği</h3>
        <p class="mb-4">AR uygulamaları, tasarımları sosyal medyada paylaşma veya aile üyeleri ile işbirliği yapma imkanı sunuyor. Farklı mobilya kombinasyonlarını kaydedip karşılaştırabilir, arkadaşlarınızdan veya iç mimarlardan görüş alabilirsiniz. Bu sosyal özellikler, satın alma sürecini daha eğlenceli ve güvenli hale getiriyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">İade Oranlarında Düşüş</h3>
        <p class="mb-4">AR teknolojisi kullanan mobilya şirketleri, iade oranlarında %40'a varan düşüşler rapor ediyor. Müşteriler, ürünü satın almadan önce mekanlarında görebildikleri için, beklentileri ile gerçek ürün arasındaki fark minimize ediliyor. Bu durum, hem müşteri memnuniyetini artırıyor hem de şirketlerin lojistik maliyetlerini düşürüyor.</p>
        
        <h3 class="text-2xl font-semibold mt-8 mb-4">Gelecek: Sanal Showroomlar</h3>
        <p class="mb-4">Gelecekte, fiziksel showroomların yerini sanal showroomlar alabilir. Müşteriler, evlerinden çıkmadan, sanal ortamda tüm koleksiyonu gezebilir, ürünleri detaylı inceleyebilir ve hemen kendi mekanlarında deneyebilir. Bu teknoloji, özellikle pandemi sonrası dönemde büyük ilgi görüyor.</p>
        
        <p class="mt-8 text-gray-600 italic">ERNADO olarak, AR teknolojisini müşterilerimize sunarak, mobilya alışverişini daha kolay, eğlenceli ve güvenli hale getiriyoruz. Uygulamalarımızla, hayalinizdeki mekanı gerçeğe dönüştürmeden önce görselleştirebilirsiniz.</p>
      `
    }
  ];

  const allNews = [featuredNews, ...smallNews];

  const handleReadMore = (newsId: number) => {
    setSelectedNews(newsId);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseNews = () => {
    setSelectedNews(null);
    document.body.style.overflow = 'unset';
  };

  const selectedNewsData = allNews.find(news => news.id === selectedNews);

  return (
    <section id="haberler" className="py-32 bg-white">
      <div className="max-w-[1440px] mx-auto px-8 lg:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
            {t('news.title')}
          </h2>
          <p className="text-gray-600 text-lg">{t('news.subtitle')}</p>
          <div className="w-16 h-[2px] bg-gray-900 mx-auto mt-6"></div>
        </motion.div>

        {/* News Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Featured News */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-1 group cursor-pointer"
          >
            <div className="relative h-[400px] rounded-3xl overflow-hidden mb-6">
              <img
                src={featuredNews.image}
                alt={featuredNews.title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute top-6 left-6">
                <span className="bg-green-700 text-white px-4 py-2 rounded-full text-xs font-light tracking-wide whitespace-nowrap">
                  {featuredNews.category}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-3">{featuredNews.date}</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-gray-600 transition-colors">
                {featuredNews.title}
              </h3>
              <p className="text-gray-600 leading-relaxed line-clamp-3">
                {featuredNews.excerpt}
              </p>
              <button 
                onClick={() => handleReadMore(featuredNews.id)}
                className="inline-block mt-4 text-gray-900 font-medium hover:underline cursor-pointer"
              >
                {t('news.readMore')}
              </button>
            </div>
          </motion.article>

          {/* Small News Grid */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-10">
            {smallNews.map((news, index) => (
              <motion.article
                key={news.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="relative h-[180px] rounded-2xl overflow-hidden mb-4">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-gray-900 text-white px-3 py-1 rounded-full text-xs font-light tracking-wide whitespace-nowrap">
                      {news.category}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-2">{news.date}</p>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 leading-tight group-hover:text-gray-600 transition-colors line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                    {news.excerpt}
                  </p>
                  <button 
                    onClick={() => handleReadMore(news.id)}
                    className="inline-block mt-2 text-sm text-gray-900 font-medium hover:underline cursor-pointer"
                  >
                    {t('news.readMore')}
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>

      {/* News Detail Modal */}
      {selectedNews && selectedNewsData && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleCloseNews}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl max-w-4xl w-full my-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseNews}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-gray-700 transition-colors z-10 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Header Image */}
            <div className="relative h-[400px] rounded-t-3xl overflow-hidden">
              <img
                src={selectedNewsData.image}
                alt={selectedNewsData.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-6 left-6">
                <span className="bg-gray-900 text-white px-4 py-2 rounded-full text-xs font-light tracking-wide whitespace-nowrap">
                  {selectedNewsData.category}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 md:p-12">
              <p className="text-sm text-gray-500 mb-4">{selectedNewsData.date}</p>
              
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedNewsData.fullContent }}
              />

              {/* Share Buttons */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-900 mb-4">Bu haberi paylaş:</p>
                <div className="flex gap-3">
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-900 hover:text-white rounded-full transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-900 hover:text-white rounded-full transition-colors cursor-pointer">
                    <i className="ri-twitter-x-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-900 hover:text-white rounded-full transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-900 hover:text-white rounded-full transition-colors cursor-pointer">
                    <i className="ri-whatsapp-fill text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}
